import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, UtensilsCrossed, Trash2, Pencil, ExternalLink, Sparkles, Upload, Loader2, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../api";
import { useToast } from "../hooks/use-toast";

export default function MenuManager() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [importing, setImporting] = useState(false);
  const [job, setJob] = useState(null);
  const [services, setServices] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const menuActive = !!services?.menus?.active;
  const upgradeHref = "/checkout?plan=menu&interval=yearly&trial=1";

  const load = async () => { setLoading(true); try { const [{ data }, s] = await Promise.all([api.menuList(), api.myServices()]); setMenus(data); setServices(s.data?.services || {}); } finally { setLoading(false); } };
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  const createEmpty = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.menuCreate({ name: newName, description: "", language: "it", status: "draft", categories: [] });
      setCreating(false); setNewName(""); navigate(`/app/menu/${data.id}`);
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || "Creazione non riuscita", variant: "destructive" }); }
  };

  const del = async (m) => { await api.menuDelete(m.id); toast({ title: "Menù eliminato" }); load(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setJob({ status: "queued", progress: 10 });
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.aiImport(fd);
      pollRef.current = setInterval(async () => {
        try {
          const res = await api.aiImportStatus(data.job_id);
          setJob(res.data);
          if (res.data.status === "done" || res.data.status === "error") {
            clearInterval(pollRef.current);
            if (res.data.status === "done") {
              // create a draft menu from result and open editor
              const r = res.data.result;
              const { data: created } = await api.menuCreate({ name: r.menu_name || "Menu importato", description: r.notes || "", language: r.language || "it", status: "draft", categories: r.categories || [] });
              toast({ title: "Menù importato!", description: "Controlla i dati estratti prima di pubblicare." });
              setImporting(false); setJob(null);
              navigate(`/app/menu/${created.id}?imported=1`);
            }
          }
        } catch { clearInterval(pollRef.current); }
      }, 1800);
    } catch (e) {
      setImporting(false); setJob(null);
      toast({ title: "Errore import", description: e?.response?.data?.detail || "Riprova", variant: "destructive" });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-2xl md:text-3xl font-extrabold">Menù digitali</h1><p className="text-muted-foreground mt-1">Crea da zero o importa il tuo menù esistente con l'AI.</p></div>
      </div>

      {services && !menuActive && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="menu-upsell">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shrink-0"><Lock className="h-5 w-5 text-amber-600" /></span>
          <div className="flex-1">
            <h3 className="font-display font-bold">Qora Menu non è attivo sul piano Free</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Importa il menù con l'AI, gestisci allergeni e pubblica il menù digitale con QR. Prova gratis 7 giorni, disdici quando vuoi.</p>
          </div>
          <Button className="bg-[#0c2d34] hover:bg-[#0c2d34]/90 text-white shrink-0" onClick={() => navigate(upgradeHref)} data-testid="menu-upsell-cta">Prova Qora Menu gratis</Button>
        </div>
      )}

      {/* big import CTA */}
      <div className="rounded-2xl btn-grad text-white p-6 mb-6 flex flex-col md:flex-row items-center gap-5">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 shrink-0"><Sparkles className="h-7 w-7" /></span>
        <div className="flex-1 text-center md:text-left">
          <h3 className="font-display text-lg font-bold">Importa il tuo menù con l'AI</h3>
          <p className="text-white/85 text-sm mt-0.5">Carica un PDF o una foto: l'AI riconosce categorie, piatti, prezzi e allergeni. Tu controlli e pubblichi.</p>
        </div>
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onFile} />
        <Button className="bg-white text-secondary hover:bg-white/90 font-semibold gap-2 shrink-0" onClick={() => menuActive ? fileRef.current?.click() : navigate(upgradeHref)} disabled={importing} data-testid="menu-import-btn">
          {importing ? <><Loader2 className="h-4 w-4 animate-spin" /> Analisi…</> : <><Upload className="h-4 w-4" /> Carica PDF/Foto</>}
        </Button>
      </div>

      <div className="flex justify-end mb-4">
        <Button variant="outline" className="gap-2" onClick={() => menuActive ? setCreating(true) : navigate(upgradeHref)} data-testid="menu-create-empty-btn"><Plus className="h-4 w-4" /> Menù vuoto</Button>
      </div>

      {loading ? <p className="text-muted-foreground">Caricamento…</p> : menus.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white p-12 text-center">
          <UtensilsCrossed className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="font-semibold mt-3">Nessun menù ancora</p>
          <p className="text-muted-foreground text-sm mt-1">Importa un PDF o creane uno vuoto.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {menus.map((m) => {
            const dishes = (m.categories || []).reduce((a, c) => a + (c.dishes?.length || 0), 0);
            return (
              <div key={m.id} className="rounded-2xl border border-border bg-white p-5 card-shadow">
                <div className="flex items-center justify-between">
                  <Badge variant={m.status === "published" ? "secondary" : "outline"} className={m.status === "published" ? "bg-emerald-50 text-emerald-600" : "text-muted-foreground"}>{m.status === "published" ? "Pubblicato" : "Bozza"}</Badge>
                  <span className="text-xs text-muted-foreground">{dishes} piatti</span>
                </div>
                <h3 className="font-display font-bold mt-3">{m.name}</h3>
                <p className="text-xs text-muted-foreground">{(m.categories || []).length} categorie</p>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={() => navigate(`/app/menu/${m.id}`)}><Pencil className="h-3.5 w-3.5" /> Modifica</Button>
                  {m.status === "published" && <Button size="sm" variant="ghost" onClick={() => window.open(`/m/${m.id}`, "_blank")}><ExternalLink className="h-4 w-4" /></Button>}
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(m)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* import progress */}
      <Dialog open={importing} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Analisi del menù in corso</DialogTitle></DialogHeader>
          <div className="py-4 text-center">
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-sm text-muted-foreground">{job?.status === "error" ? job.error : "L'AI sta leggendo il documento ed estraendo piatti, prezzi e allergeni. Puoi attendere qui."}</p>
            <div className="mt-4 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full btn-grad transition-all" style={{ width: `${job?.progress || 10}%` }} /></div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nuovo menù vuoto</DialogTitle></DialogHeader>
          <div><Label className="text-sm">Nome del menù</Label><Input value={newName} onChange={(e) => setNewName(e.target.value)} className="h-10 mt-1" placeholder="Es. Menù alla carta" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setCreating(false)}>Annulla</Button><Button className="btn-grad text-white font-semibold" onClick={createEmpty}>Crea</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
